import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import { ClipboardList, Handshake, LogOut, Menu, UserRound, X } from 'lucide-react';

interface ManagerLayoutProps {
  children: React.ReactNode;
}

const ManagerLayout: React.FC<ManagerLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { confirm } = useConfirm();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    const shouldLogout = await confirm({
      title: 'Cerrar sesión',
      message: '¿Seguro que deseas cerrar sesión?',
      confirmText: 'Cerrar sesión',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldLogout) return;

    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const isDelegationsActive = location.pathname === '/manager/delegations';
  const isOutreachActive = location.pathname === '/manager/outreach';

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {isMobileSidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar menú"
          className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[1px] md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 h-full flex-shrink-0 border-r border-slate-200 bg-white transition-transform duration-300 md:static md:z-auto md:translate-x-0 md:transition-[width] md:duration-300 ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${isSidebarOpen ? 'md:w-64' : 'md:w-20'} w-80 max-w-[86vw] md:max-w-none`}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 p-4">
            {isSidebarOpen ? (
              <div className="min-w-0">
                <h1 className="whitespace-nowrap text-xl font-bold text-slate-800">Manager Panel</h1>
                <p className="truncate text-xs text-slate-500">
                  {user?.managedComplexName || 'Conjunto'}
                </p>
              </div>
            ) : (
              <span className="sr-only">Manager Panel</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="inline-flex rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden"
                aria-label="Cerrar panel"
              >
                <X size={20} />
              </button>
              <button
                type="button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="hidden rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:inline-flex"
                aria-label={isSidebarOpen ? 'Contraer panel' : 'Expandir panel'}
              >
                {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          <nav className="flex-1 space-y-2 overflow-y-auto p-4">
            <Link
              to="/manager/delegations"
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors ${
                isDelegationsActive
                  ? 'bg-blue-50 font-medium text-blue-700 shadow-sm'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
              title={!isSidebarOpen ? 'Delegaciones' : undefined}
            >
              <Handshake
                size={20}
                className={isDelegationsActive ? 'text-blue-700' : 'text-slate-500'}
              />
              {isSidebarOpen && (
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">Delegaciones</div>
                  <div className="truncate text-xs text-slate-500">Gestión por asamblea</div>
                </div>
              )}
            </Link>
            <Link
              to="/manager/outreach"
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors ${
                isOutreachActive
                  ? 'bg-blue-50 font-medium text-blue-700 shadow-sm'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
              title={!isSidebarOpen ? 'Seguimiento' : undefined}
            >
              <ClipboardList
                size={20}
                className={isOutreachActive ? 'text-blue-700' : 'text-slate-500'}
              />
              {isSidebarOpen && (
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">Seguimiento</div>
                  <div className="truncate text-xs text-slate-500">Contacto sin poder</div>
                </div>
              )}
            </Link>
          </nav>

          <div className="border-t border-slate-200 p-4">
            {isSidebarOpen ? (
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-sm font-medium text-slate-800">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="break-all text-xs text-slate-500">{user?.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <UserRound size={16} />
                  Mi perfil
                </button>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                >
                  <LogOut size={16} />
                  Cerrar sesión
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="w-full rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-50"
                  title="Mi perfil"
                >
                  <UserRound size={20} />
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full rounded-xl p-2 text-red-600 transition-colors hover:bg-red-50"
                  title="Cerrar sesión"
                >
                  <LogOut size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="relative flex-1 overflow-y-auto">
        <button
          type="button"
          onClick={() => setIsMobileSidebarOpen(true)}
          className="fixed left-4 top-4 z-30 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur md:hidden"
        >
          <Menu size={18} />
          Menú
        </button>
        <div className="h-full">{children}</div>
      </main>
    </div>
  );
};

export default ManagerLayout;
